import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { reduce } from '@atlaskit/adf-utils/reduce';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';

export type ContentFeatures = {
	rawLength: number;
	textLength: number;
	wordCount: number;
	imageCount: number;
	inlineMacroCount: number;
	macroCount: number;
	tableCount: number;
	mediaFiles: string[];
	looms: { id: string; url: string }[];
};

// helper function to traverse ADF tree while skipping expand nodes
const reduceSkipExpand = <T>(
	node: ADFEntity,
	reducer: (acc: T, node: ADFEntity) => T,
	initialValue: T,
): T => {
	// skip if this is an expand node
	if (node.type === 'expand' || node.type === 'nestedExpand') {
		return initialValue;
	}

	// process current node
	let result = reducer(initialValue, node);

	// process child nodes if they exist
	if (node.content) {
		result = (node.content as ADFEntity[]).reduce(
			(acc, child) => reduceSkipExpand(child, reducer, acc),
			result,
		);
	}

	return result;
};

const wordCount = (text?: string) => {
	if (text) {
		const matches = text.match(/\S+/g);
		if (matches) {
			return matches.length;
		}
	}
	return 0;
};

const processADFNode = (currentValue: ContentFeatures, node: ADFEntity): ContentFeatures => {
	switch (node.type) {
		case 'text':
			return {
				...currentValue,
				textLength: currentValue.textLength + (node.text ? node.text.length : 0),
				wordCount: currentValue.wordCount + wordCount(node.text),
			};
		case 'media':
			return {
				...currentValue,
				imageCount: currentValue.imageCount + 1,
				mediaFiles:
					node.attrs?.type === 'file' && node.attrs?.id
						? currentValue.mediaFiles.concat(node.attrs.id)
						: currentValue.mediaFiles,
			};
		case 'inlineExtension':
			// Legacy quirk (inlineMacroCount derived from macroCount) preserved for the
			// gate-OFF path so its output is byte-identical to before. The gate-ON
			// single pass corrects this — see applyADFNode.
			return {
				...currentValue,
				inlineMacroCount: currentValue.macroCount + 1,
			};
		case 'extension':
			return {
				...currentValue,
				macroCount: currentValue.macroCount + 1,
			};
		case 'table':
			return {
				...currentValue,
				tableCount: currentValue.tableCount + 1,
			};
		case 'embedCard':
			const url = node.attrs?.url;

			if (!url.includes('loom')) {
				return currentValue;
			}

			const videoId = url?.split('/').pop().split('?')[0];

			return {
				...currentValue,
				looms: videoId ? currentValue.looms.concat({ id: videoId, url }) : currentValue.looms,
			};
	}
	return currentValue;
};

export const extractContentFeatures = (adf: JSONDocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		rawLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		imageCount: 0,
		inlineMacroCount: 0,
		macroCount: 0,
		tableCount: 0,
		mediaFiles: [],
		looms: [],
	};

	return reduce<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};

// Extract content features while skipping expand nodes
export const extractContentFeaturesSkipExpand = (adf: JSONDocNode): ContentFeatures => {
	const initialFeatures: ContentFeatures = {
		rawLength: JSON.stringify(adf).length,
		textLength: 0,
		wordCount: 0,
		imageCount: 0,
		inlineMacroCount: 0,
		macroCount: 0,
		tableCount: 0,
		mediaFiles: [],
		looms: [],
	};

	return reduceSkipExpand<ContentFeatures>(
		adf,
		(currentValue, node: ADFEntity) => processADFNode(currentValue, node),
		initialFeatures,
	);
};

const makeInitialFeatures = (rawLength: number): ContentFeatures => ({
	rawLength,
	textLength: 0,
	wordCount: 0,
	imageCount: 0,
	inlineMacroCount: 0,
	macroCount: 0,
	tableCount: 0,
	mediaFiles: [],
	looms: [],
});

// Mutating equivalent of processADFNode, used by the single-pass (gate-ON) path.
// Matches the legacy processADFNode EXCEPT it corrects inlineMacroCount to count
// inlineExtension nodes; the legacy path keeps the historic `macroCount + 1`
// quirk so gate-OFF output is unchanged.
const applyADFNode = (acc: ContentFeatures, node: ADFEntity): void => {
	switch (node.type) {
		case 'text':
			acc.textLength += node.text ? node.text.length : 0;
			acc.wordCount += wordCount(node.text);
			return;
		case 'media':
			acc.imageCount += 1;
			if (node.attrs?.type === 'file' && node.attrs?.id) {
				acc.mediaFiles.push(node.attrs.id);
			}
			return;
		case 'inlineExtension':
			// Corrected: count inlineExtension nodes (legacy processADFNode used
			// macroCount + 1). Gated ON via read_time_parse_dedup.
			acc.inlineMacroCount += 1;
			return;
		case 'extension':
			acc.macroCount += 1;
			return;
		case 'table':
			acc.tableCount += 1;
			return;
		case 'embedCard': {
			const url = node.attrs?.url;
			if (!url || !url.includes('loom')) {
				return;
			}
			const videoId = url.split('/').pop()?.split('?')[0];
			if (videoId) {
				acc.looms.push({ id: videoId, url });
			}
			return;
		}
	}
};

// Above this many ADF nodes an exact read-time estimate is not meaningful (the
// document is structurally huge, e.g. a multi-thousand-row table), so the walk
// stops early and the badge shows a capped "{floor}+ min read". Node count is
// the exact cost driver for parse/walk/render. Calibration: a ~60-min prose
// article is well under ~20k nodes; a 10k-row table is ~190k nodes. Tunable.
export const READ_TIME_MAX_NODES = 50_000;

/**
 * Single traversal producing BOTH the full-document features (equivalent to
 * `extractContentFeatures`) and the expand-skipping features (equivalent to
 * `extractContentFeaturesSkipExpand`), plus the ADF `nodeCount`. The walk stops
 * early once the document exceeds `READ_TIME_MAX_NODES` (nodeCount settles at
 * the cap + 1), since past that point read time is shown as a lower bound.
 *
 * `rawLength` is the serialized ADF string length (analytics metadata only,
 * stamped onto both results). Callers that hold the string pass its length;
 * it is omitted (defaults to 0) when working from a live document with no
 * serialized form.
 */
export const extractAllContentFeatures = (
	adf: JSONDocNode,
	rawLength?: number,
): { features: ContentFeatures; featuresSkipExpand: ContentFeatures; nodeCount: number } => {
	const features = makeInitialFeatures(rawLength ?? 0);
	const featuresSkipExpand = makeInitialFeatures(rawLength ?? 0);
	let nodeCount = 0;

	const visit = (node: ADFEntity, countSkip: boolean): void => {
		nodeCount += 1;
		if (nodeCount > READ_TIME_MAX_NODES) {
			return;
		}
		applyADFNode(features, node);
		const isExpand = node.type === 'expand' || node.type === 'nestedExpand';
		const skipHere = countSkip && !isExpand;
		if (skipHere) {
			applyADFNode(featuresSkipExpand, node);
		}
		const children = node.content as ADFEntity[] | undefined;
		if (children) {
			for (const child of children) {
				if (nodeCount > READ_TIME_MAX_NODES) {
					break;
				}
				// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- single pre-order DFS; each ADF node is visited exactly once (O(total nodes)), not quadratic
				visit(child, skipHere);
			}
		}
	};

	visit(adf as unknown as ADFEntity, true);

	return { features, featuresSkipExpand, nodeCount };
};
